import { Controller } from "stimulus"
import { celebratePoints } from "../services/points_celebration_service"

const TOTAL_STEPS = 6
// Maps step number → response field for the Yes/No questions (steps 2-5).
const YES_NO_STEPS = {
  2: "has_collector_contact",
  3: "relies_on_credit",
  4: "borrowed_for_payment",
  5: "has_payoff_plan",
}

export default class extends Controller {
  static get targets() {
    return [
      "topbar",
      "stepIndicator",
      "stepCurrent",
      "step1",
      "step2",
      "step3",
      "step4",
      "step5",
      "step6",
      "completion",
      "step1Next",
      "forwardBtn",
      "debtTypeOption",
      "yesNoOption",
      "scoreOption",
      "completionName",
      "feedbackLow",
      "feedbackMid",
      "feedbackHigh",
      "feedbackCritical",
      "counselorBlock",
      "backToQuestBtn",
    ]
  }

  static get values() {
    return {
      currentStep: Number,
      responses: Object,
      userName: String,
      completeUrl: String,
      completed: Boolean,
      edit: Boolean,
      existingData: Object,
    }
  }

  connect() {
    this._disconnected = false
    // Edit mode reopens a completed check-in at step 1, pre-filled from the saved
    // answers, so members can revise them. Otherwise a completed check-in shows
    // the completion screen and a fresh one starts blank at step 1.
    if (this.completedValue && !this.editValue) {
      this.responsesValue = this.existingDataValue || {}
      this.showCompletion(false)
    } else {
      this.responsesValue = this.editValue ? this.existingDataValue || {} : {}
      this.goToStep(1)
    }
  }

  disconnect() {
    // Turbo can swap the DOM (e.g. the completion-screen link navigates away)
    // while the completion PATCH is still in flight. Mark ourselves gone so the
    // fetch callbacks bail before touching now-detached targets.
    this._disconnected = true
  }

  toggleDebtType(event) {
    const button = event.currentTarget
    const type = button.dataset.debtType
    const responses = { ...this.responsesValue }
    const selected = new Set(responses.debt_types || [])

    if (selected.has(type)) {
      selected.delete(type)
      this._setOptionSelected(button, false)
    } else {
      selected.add(type)
      this._setOptionSelected(button, true)
    }

    responses.debt_types = Array.from(selected)
    this.responsesValue = responses

    this.step1NextTarget.disabled = selected.size === 0
  }

  nextFromStep1() {
    const types = (this.responsesValue.debt_types || [])
    if (types.length === 0) return
    this.goToStep(2)
  }

  answerYesNo(event) {
    const button = event.currentTarget
    const field = button.dataset.field
    const value = button.dataset.value === "true"

    this.responsesValue = { ...this.responsesValue, [field]: value }
    this.goToStep(this.currentStepValue + 1)
  }

  selectScore(event) {
    const score = Number(event.currentTarget.dataset.score)
    const responses = { ...this.responsesValue, feeling_score: score }
    this.responsesValue = responses

    this.showCompletion(true)
  }

  back() {
    if (this.currentStepValue <= 1) return
    this.goToStep(this.currentStepValue - 1)
  }

  // Move forward keeping the already-saved answer. Only reachable via the forward
  // button, which is shown only once the current step has an answer (going back or
  // editing); the guard re-checks so a stray click can't skip an unanswered step.
  forward() {
    const step = this.currentStepValue
    if (!this._stepAnswered(step)) return
    if (step >= TOTAL_STEPS) {
      this.showCompletion(true)
    } else {
      this.goToStep(step + 1)
    }
  }

  goToStep(stepNum) {
    // A fast double-tap on a Yes/No button can advance twice; clamp so we never
    // reach for a step target that doesn't exist (e.g. step7Target).
    if (stepNum < 1 || stepNum > TOTAL_STEPS) return
    this.currentStepValue = stepNum
    for (let i = 1; i <= TOTAL_STEPS; i++) {
      const target = this[`step${i}Target`]
      if (target) target.hidden = i !== stepNum
    }
    this.completionTarget.hidden = true
    this.stepIndicatorTarget.hidden = false
    this.stepCurrentTarget.textContent = String(stepNum)
    if (this.hasTopbarTarget) this.topbarTarget.hidden = false

    this._reflectStepSelections(stepNum)
    this._updateForwardButton(stepNum)
  }

  // The forward button for the current step is shown only when that step already
  // has a saved answer — i.e. the user reached it by going back or is editing.
  // On a first pass the step is unanswered, so it stays hidden and selecting an
  // answer auto-advances as before.
  _updateForwardButton(stepNum) {
    if (!this.hasForwardBtnTarget) return
    const answered = this._stepAnswered(stepNum)
    this.forwardBtnTargets.forEach(btn => {
      if (Number(btn.dataset.step) !== stepNum) return
      btn.hidden = !answered
    })
  }

  // Whether the given step (2-6) already holds an answer. Step 1 has its own
  // dedicated Next button and is never queried here.
  _stepAnswered(stepNum) {
    const field = YES_NO_STEPS[stepNum]
    if (field) return typeof this.responsesValue[field] === "boolean"
    if (stepNum === TOTAL_STEPS) return this.responsesValue.feeling_score != null

    return true
  }

  showCompletion(submit) {
    for (let i = 1; i <= TOTAL_STEPS; i++) {
      const target = this[`step${i}Target`]
      if (target) target.hidden = true
    }
    this.stepIndicatorTarget.hidden = true
    this.completionTarget.hidden = false
    // Keep the topbar (with its close "X") visible on the completion screen so
    // there's always a way out of the flow.
    if (this.hasTopbarTarget) this.topbarTarget.hidden = false

    if (this.hasCompletionNameTarget) {
      this.completionNameTarget.textContent = this.userNameValue
    }

    this._renderFeedback()

    // The completion CTA routes back to the Debt Quest dashboard; reveal it once
    // the completion screen is shown.
    if (this.hasBackToQuestBtnTarget) this.backToQuestBtnTarget.hidden = false

    if (submit) this._submitResponses()
  }

  _reflectStepSelections(stepNum) {
    if (stepNum === 1) {
      const selected = new Set(this.responsesValue.debt_types || [])
      this.debtTypeOptionTargets.forEach(btn => {
        this._setOptionSelected(btn, selected.has(btn.dataset.debtType))
      })
      this.step1NextTarget.disabled = selected.size === 0
      return
    }

    if (YES_NO_STEPS[stepNum]) {
      const field = YES_NO_STEPS[stepNum]
      const current = this.responsesValue[field]
      this.yesNoOptionTargets.forEach(btn => {
        if (btn.dataset.field !== field) return
        const matches = current !== undefined && (btn.dataset.value === "true") === current
        this._setOptionSelected(btn, matches)
      })
    }

    if (stepNum === 6) {
      const current = this.responsesValue.feeling_score
      this.scoreOptionTargets.forEach(btn => {
        this._setOptionSelected(btn, Number(btn.dataset.score) === current)
      })
    }
  }

  // Border is set inline (see show.html.slim — Tailwind border classes don't render
  // on <button> with preflight disabled). We swap only the color here.
  _setOptionSelected(button, selected) {
    button.classList.toggle("tw-bg-indigo-50", selected)
    button.classList.toggle("tw-bg-gray-lighter", !selected)
    button.style.borderColor = selected ? "#5A50E6" : "#E5E7EB"
    button.setAttribute("aria-pressed", selected ? "true" : "false")
  }

  // Composite "concern score" from the survey answers. Range: 1-9.
  // - has_collector_contact (Q2 yes): +1
  // - relies_on_credit (Q3 yes): +1
  // - borrowed_for_payment (Q4 yes): +1
  // - has_payoff_plan (Q5 NO — note: reversed): +1
  // - feeling_score (Q6 1-5 scale): adds its raw value
  _computeConcernScore() {
    const r = this.responsesValue
    let score = Number(r.feeling_score) || 0
    if (r.has_collector_contact) score += 1
    if (r.relies_on_credit) score += 1
    if (r.borrowed_for_payment) score += 1
    if (r.has_payoff_plan === false) score += 1
    return score
  }

  _concernLevel() {
    const score = this._computeConcernScore()
    return (
      score >= 7 ? "critical" :
      score >= 5 ? "high" :
      score >= 3 ? "mid" :
                   "low"
    )
  }

  _renderFeedback() {
    const level = this._concernLevel()

    ;["Low", "Mid", "High", "Critical"].forEach(name => {
      const target = this[`feedback${name}Target`]
      if (target) target.hidden = name.toLowerCase() !== level
    })

    if (this.hasCounselorBlockTarget) {
      this.counselorBlockTarget.hidden = level !== "high" && level !== "critical"
    }
  }

  _submitResponses() {
    // Guard against a double-tap on the score button firing two PATCHes: the
    // response stays single, but each request re-enqueues an ActionLog server-side.
    if (this._submitting) return
    this._submitting = true

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const r = this.responsesValue
    const payload = {
      debt_types: r.debt_types || [],
      feeling_score: r.feeling_score,
    }
    // Only send Yes/No answers that were actually given. Omitting unanswered
    // fields lets the backend distinguish an explicit "No" (false) from "no
    // signal" (nil) — forcing booleans here would collapse both into false.
    for (const field of Object.values(YES_NO_STEPS)) {
      if (typeof r[field] === "boolean") payload[field] = r[field]
    }
    fetch(this.completeUrlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrf,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload)
    }).then((response) => {
      if (this._disconnected) return
      // An expired session makes Devise redirect to sign-in (302 → 200 HTML).
      // fetch transparently follows that redirect, so response.ok alone would
      // mark an unsaved completion as saved. Treat any redirect as a failure.
      if (!response.ok || response.redirected) {
        throw new Error(`DebtCheckIn: PATCH /complete returned ${response.status}`)
      }
      this.completionTarget.dataset.saved = "true"
      // Celebrate only on the user's first-ever completion.
      return response.json().then((data) => {
        if (this._disconnected) return
        if (data && data.first_completion) celebratePoints(this.element)
      }).catch((jsonErr) => {
        // The save already succeeded (dataset.saved set above); only the JSON body
        // was unreadable. Surface it so a silent contract regression (a non-JSON
        // 200) stays observable, without flipping the save state to "error".
        globalThis.Rollbar?.warning(jsonErr, { detail: "DebtCheckIn: completion JSON parse failed; celebration skipped" })
      })
    }).catch((err) => {
      // If the controller is gone the user navigated away; the save may well
      // have succeeded, so don't surface a misleading error for a dead target.
      if (this._disconnected) return
      this.completionTarget.dataset.saved = "error"
      globalThis.Rollbar?.error(err, { detail: "DebtCheckIn: failed to record completion" })
    }).finally(() => {
      this._submitting = false
    })
  }
}
